import * as React from 'react';
import { cva, type VariantProps } from '../../lib/cva';
import { cn } from '../../lib/cn';

// Design-system button. Token-driven variants + a visible focus ring for a11y.
// Vibrant pill: fully-rounded, saturated SOLID fills (no gradient), bold label,
// tinted secondary. Crisp press feedback, no hover lift.
const buttonVariants = cva(
  // appearance-none + border-0 here (not just the .ds reset) so buttons that
  // carry `.ds` on themselves — vs. as an ancestor — still drop native chrome.
  'relative inline-flex cursor-pointer select-none appearance-none items-center justify-center gap-1.5 rounded-full border-0 ' +
    'text-sm font-semibold tracking-tight whitespace-nowrap ring-1 ring-inset ring-transparent ' +
    'transition-[background-color,box-shadow,transform,filter] duration-150 ease-out active:scale-[0.97] ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ' +
    'disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Filled pills: solid fill + a hairline glossy top highlight (inset) and
        // a soft 1px drop for just-enough depth — no gradient, no hover lift.
        primary:
          'bg-primary text-primary-foreground shadow-[0_1px_2px_oklch(0_0_0/0.12),inset_0_1px_0_oklch(1_0_0/0.18)] hover:bg-primary/90',
        accent:
          'bg-accent text-accent-foreground shadow-[0_1px_2px_oklch(0_0_0/0.12),inset_0_1px_0_oklch(1_0_0/0.25)] hover:brightness-105',
        destructive:
          'bg-destructive text-destructive-foreground shadow-[0_1px_2px_oklch(0_0_0/0.12),inset_0_1px_0_oklch(1_0_0/0.18)] hover:bg-destructive/90',
        // Soft neutral fill — for quiet/secondary actions (no indigo, not a
        // stark white outline). Reads cleanly in tables and toolbars.
        secondary: 'bg-secondary text-secondary-foreground ring-border/60 hover:bg-secondary/70',
        outline: 'bg-card text-foreground ring-border hover:bg-secondary',
        ghost: 'hover:bg-secondary hover:text-foreground',
      },
      size: {
        sm: 'h-8 px-3.5 text-xs',
        md: 'h-9 px-5',
        lg: 'h-11 px-6 text-[15px]',
        icon: 'size-9 px-0',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  }
);

export interface ButtonProps
  extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {}

export function Button({ className, variant, size, ...props }: ButtonProps) {
  return <button className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}

export { buttonVariants };
